/**
 * Crypto Payment Service
 *
 * API client for the "Pay with Crypto" tab on the payment page.
 * Mirrors paymentSessionService style and talks to
 * /api/v1/payment-session/:token/crypto/* on the backend.
 */

import axios from 'axios';

const ENV = process.env.REACT_APP_ENV || 'local';
const ENV_URLS = {
  local: process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3001',
  development:
    process.env.REACT_APP_DEV_API_URL || 'https://betagate.kubercrypto.io',
  staging:
    process.env.REACT_APP_STAGING_API_URL || 'https://betagate.kubercrypto.io',
  production:
    process.env.REACT_APP_PROD_API_URL || 'https://apigate.kubercrypto.io',
};
const BASE_URL = ENV_URLS[ENV] || ENV_URLS.local;

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

client.interceptors.response.use(
  (response) => {
    if (response.data && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      return Promise.reject({
        type: status === 401 ? 'session' : 'error',
        status,
        message: data?.message || data?.error || 'An error occurred',
        errorCode: data?.errorCode,
      });
    }
    if (error.request) {
      return Promise.reject({
        type: 'network',
        status: 0,
        message: 'Network error. Please check your connection.',
      });
    }
    return Promise.reject({
      type: 'unknown',
      status: 0,
      message: error.message || 'An unexpected error occurred',
    });
  }
);

class CryptoPaymentService {
  async listTokens(token) {
    return client.get(`/api/v1/payment-session/${token}/crypto/tokens`);
  }

  async getQuote(token, { tokenId, network }) {
    return client.get(`/api/v1/payment-session/${token}/crypto/quote`, {
      params: { tokenId, network },
    });
  }

  async createAddress(token, { quoteId }) {
    return client.post(`/api/v1/payment-session/${token}/crypto/address`, {
      quoteId,
    });
  }

  async getStatus(token, { signal } = {}) {
    return client.get(`/api/v1/payment-session/${token}/crypto/status`, {
      signal,
    });
  }

  /**
   * Commit the order to the crypto-direct rail. Allocates a per-order BitGo
   * address (idempotent — repeated calls return the existing one).
   *
   * Called by the chooser when the customer picks "Pay with Crypto" on a
   * mixed-rail payment link. Crypto-only links don't need to call this —
   * the backend pre-generates the address at order create.
   *
   * Returns the AddressResponseDto shape:
   *   { depositAddress, network, networkDisplayName, tokenSymbol, tokenId,
   *     expectedAmount, decimals, qrPayload, minConfirmations, paymentMode }
   */
  async commit(token) {
    return client.post(`/api/v1/payment-session/${token}/crypto/commit`, {});
  }
}

export default new CryptoPaymentService();
